// Capacitor 네이티브(Android) 래퍼 전용 - 파일 다운로드 가로채기.
// index.js에서 initNativeApp()과 나란히 한 번만 호출한다. 웹(Capacitor.isNativePlatform()이
// false)에서는 initNativeDownloads()가 즉시 리턴하는 완전한 no-op이고, 프로토타입도 전혀
//건드리지 않는다.
//
// 왜 필요한가: 이 저장소의 다운로드는 전부 아래 두 패턴 중 하나다.
//   (a) URL.createObjectURL(blob) + <a download> 생성 후 click()/dispatchEvent(MouseEvent) —
//       대부분의 엑셀/PDF 내보내기, RagSourceLink, 채팅 파일 다운로드 등.
//   (b) window.open(url) — blob: URL을 새 창으로 열거나(명세표 미리보기 등),
//       같은 출처의 파일 서빙 API(/api/.../file/...)를 직접 새 탭으로 여는 경우.
// 일반 Android WebView는 blob:/data: 다운로드를 기본적으로 처리하지 못하고, window.open도
// Capacitor 환경에서는 새 창을 띄우지 못해 아무 일도 일어나지 않는다. 그래서 네이티브에서만
// HTMLAnchorElement.prototype.click/dispatchEvent와 window.open을 패치해서, 바이트를 직접
// fetch로 받아 @capacitor/filesystem에 저장하고 열기/공유를 제안한다.
import { Capacitor } from '@capacitor/core';

// ---- 순수 로직 (유닛 테스트 대상) ----

export function isBlobOrDataUrl(url) {
  return typeof url === 'string' && /^(blob|data):/i.test(url);
}

// SPA 라우트(예: /partnermng/add, /mainchat)는 건드리지 않고, 실제 파일을 내려주는
// API 경로(/api/.../file/..., /api/.../messages/info/file/...)만 다운로드로 취급한다.
export function isSameOriginApiFileUrl(url, origin) {
  if (typeof url !== 'string' || !url || !origin) return false;
  let parsed;
  try {
    parsed = new URL(url, origin);
  } catch {
    return false;
  }
  if (parsed.origin !== origin) return false;
  return /\/api\/[^/]+\/(?:file|messages\/info\/file)(?:\/|$)/i.test(parsed.pathname);
}

export function shouldInterceptWindowOpenUrl(url, origin) {
  return isBlobOrDataUrl(url) || isSameOriginApiFileUrl(url, origin);
}

// anchor: DOM Element(또는 테스트에서는 { getAttribute, hasAttribute } 흉내만 내는 객체)
export function isDownloadAnchor(anchor, origin) {
  if (!anchor || typeof anchor.getAttribute !== 'function') return false;
  if (typeof anchor.hasAttribute === 'function' && !anchor.hasAttribute('download')) {
    return false;
  }
  const href = anchor.getAttribute('href') || '';
  if (!href) return false;
  return isBlobOrDataUrl(href) || isSameOriginApiFileUrl(href, origin);
}

export function filenameFromUrl(url, fallback = 'download') {
  if (!url) return fallback;
  try {
    const parsed = new URL(url, 'http://local.invalid');
    const last = parsed.pathname.split('/').filter(Boolean).pop();
    return last ? decodeURIComponent(last) : fallback;
  } catch {
    const parts = String(url).split('/');
    return parts[parts.length - 1] || fallback;
  }
}

export function getAnchorFilename(anchor, fallbackHref) {
  const dl = anchor?.getAttribute?.('download');
  if (dl) return dl;
  const href = fallbackHref ?? anchor?.getAttribute?.('href') ?? '';
  return filenameFromUrl(href);
}

// RagSourceLink.js의 fileNameFromHeader와 동일한 규칙 (Content-Disposition 파싱).
export function filenameFromContentDisposition(header, fallback = 'download') {
  if (!header) return fallback;
  const star = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      // 아래 일반 규칙으로 폴백
    }
  }
  const plain = header.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
}

export function sanitizeFilename(name) {
  const base = (name == null ? '' : String(name)).trim() || 'download';
  return base.replace(/[\\/:*?"<>|]/g, '_').slice(0, 180);
}

// Blob -> base64. 큰 파일에서 String.fromCharCode.apply(null, 전체배열)을 한 번에 하면
// 콜스택/성능 문제가 날 수 있어 청크 단위로 나눠 돈다.
export function arrayBufferToBase64(buffer, chunkSize = 0x8000) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  if (typeof btoa === 'function') return btoa(binary);
  // jsdom 등 btoa가 없는 환경 폴백 (테스트 전용 경로)
  // eslint-disable-next-line no-undef
  return Buffer.from(binary, 'binary').toString('base64');
}

// ---- 부수효과 (네이티브에서만 실행) ----

const DOWNLOAD_FOLDER = 'KMES';
let installed = false;

function readAuthHeader() {
  try {
    const token = localStorage.getItem('access_token');
    if (!token) return null;
    return 'Bearer ' + JSON.parse(token);
  } catch {
    return null;
  }
}

async function fetchAsBlob(url) {
  if (isBlobOrDataUrl(url)) {
    // 같은 문서 안의 blob: URL(또는 data: URL)은 인증 없이 그대로 fetch로 읽을 수 있다 —
    // 이미 axios로 인증까지 끝낸 뒤 만든 blob이기 때문.
    const res = await fetch(url);
    return { blob: await res.blob(), headers: res.headers };
  }
  const auth = readAuthHeader();
  const res = await fetch(url, { headers: auth ? { Authorization: auth } : {} });
  if (!res.ok) throw new Error(`다운로드 실패: HTTP ${res.status}`);
  return { blob: await res.blob(), headers: res.headers };
}

function notifyDownloaded(fileName, openFile) {
  try {
    window.dispatchEvent(new CustomEvent('kmes:file-downloaded', { detail: { fileName, open: openFile } }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[nativeDownload] toast event dispatch failed', e);
  }
}

async function saveAndOffer(blob, suggestedName) {
  // 플러그인은 실제 기기에서만 의미가 있어 쓰는 시점에 불러온다(웹 번들/테스트에 영향 없음).
  const [{ Filesystem, Directory }, shareModule, fileOpenerModule] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share').catch(() => null),
    import('@capacitor-community/file-opener').catch(() => null),
  ]);

  const fileName = sanitizeFilename(suggestedName);
  const path = `${DOWNLOAD_FOLDER}/${fileName}`;
  const buffer = await blob.arrayBuffer();
  const base64 = arrayBufferToBase64(buffer);

  await Filesystem.writeFile({
    path,
    data: base64,
    directory: Directory.Documents,
    recursive: true,
  });
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Documents });

  const openFile = async () => {
    try {
      if (!fileOpenerModule?.FileOpener) throw new Error('file-opener 플러그인 없음');
      await fileOpenerModule.FileOpener.open({ filePath: uri, contentType: blob.type || undefined });
    } catch (e) {
      try {
        if (!shareModule?.Share) throw e;
        await shareModule.Share.share({ title: fileName, url: uri });
      } catch (shareErr) {
        // eslint-disable-next-line no-console
        console.warn('[nativeDownload] open/share 모두 실패', shareErr);
      }
    }
  };

  notifyDownloaded(fileName, openFile);
  return openFile;
}

async function interceptAnchor(anchor) {
  const href = anchor.getAttribute('href');
  const suggested = getAnchorFilename(anchor, href);
  try {
    const { blob } = await fetchAsBlob(href);
    await saveAndOffer(blob, suggested);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[nativeDownload] anchor 다운로드 가로채기 실패', e);
  }
}

async function interceptWindowOpenUrl(url) {
  try {
    const { blob, headers } = await fetchAsBlob(url);
    const cd = typeof headers?.get === 'function' ? headers.get('content-disposition') : null;
    const name = filenameFromContentDisposition(cd, filenameFromUrl(url));
    const openFile = await saveAndOffer(blob, name);
    // window.open으로 연 PDF는 "미리보기" 의도(명세표 미리보기 등)라 저장 즉시 뷰어로 연다.
    if (blob.type === 'application/pdf' || /\.pdf$/i.test(name)) await openFile();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[nativeDownload] window.open 다운로드 가로채기 실패', e);
  }
}

// 네이티브 여부만 필요한 호출부(PdfViewerDialog 등)를 위한 얇은 헬퍼. nativeApp.js에는
// 아직 이런 범용 헬퍼가 없어 여기 둔다 - biometricLock.js의 isNative()와 동일한 패턴.
export function isNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

// PDF 미리보기 전용 헬퍼. Android WebView는 PDF를 iframe으로 그리지 못해 미리보기 Dialog가
// 빈 화면이 되므로, 네이티브에서는 이걸로 iframe 대신 "저장 후 폰 PDF 뷰어로 바로 열기"를 한다.
// blobOrUrl에는 이미 메모리에 있는 Blob(가장 흔한 경우 - 미리보기는 보통 axios로 받은 블랍을
// URL.createObjectURL()로 감싼 것)이나 blob:/같은 출처 URL을 그대로 넘기면 된다.
// saveAndOffer()가 띄우는 토스트(나중에 다시 열기)는 그대로 살려두고, 여기서는 "미리보기"를
// 누른 사용자 의도에 맞게 저장 즉시 뷰어를 연다. 실패 시 호출부에서 스낵바로 안내할 수 있도록
// 에러를 그대로 던진다. 웹에서는 완전한 no-op.
export async function openPdfNatively(blobOrUrl, fileName) {
  if (!isNativeApp()) return;
  const blob = blobOrUrl instanceof Blob ? blobOrUrl : (await fetchAsBlob(blobOrUrl)).blob;
  const openFile = await saveAndOffer(blob, fileName || 'preview.pdf');
  await openFile();
}

export function initNativeDownloads() {
  if (!Capacitor.isNativePlatform()) return; // 웹: 완전한 no-op, 프로토타입 패치 없음
  if (installed) return;
  installed = true;

  const origin = window.location.origin;
  const AnchorProto = window.HTMLAnchorElement.prototype;
  const originalClick = AnchorProto.click;
  const originalDispatchEvent = AnchorProto.dispatchEvent;

  AnchorProto.click = function patchedAnchorClick(...args) {
    if (isDownloadAnchor(this, origin)) {
      interceptAnchor(this);
      return undefined;
    }
    return originalClick.apply(this, args);
  };

  AnchorProto.dispatchEvent = function patchedAnchorDispatchEvent(event) {
    // file-saver는 click()이 아니라 dispatchEvent(new MouseEvent('click'))로 다운로드를 건다.
    if (event && event.type === 'click' && isDownloadAnchor(this, origin)) {
      interceptAnchor(this);
      return true;
    }
    return originalDispatchEvent.call(this, event);
  };

  const originalWindowOpen = window.open ? window.open.bind(window) : null;
  window.open = function patchedWindowOpen(url, target, features) {
    if (typeof url === 'string' && shouldInterceptWindowOpenUrl(url, origin)) {
      interceptWindowOpenUrl(url);
      return null;
    }
    return originalWindowOpen ? originalWindowOpen(url, target, features) : null;
  };
}

export default initNativeDownloads;
